/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import store from '../../redux/reduxStore_authorizedApp';
import { actions as systemNotificationActions } from '../../redux/ducks/systemNotification';

import actionNames from './actionNames';
import { updateMainUser } from './actions';
import { getLoggedInUser, getChangePasswordFlowData, getEnableMfaFlowData } from './reduxSelectors';
import SessionService from '../../../apiServices/SessionService';
import UserService from '../../../apiServices/UserService';
import EnableMfaModel from '../../../models/EnableMfaModel';
import FileModel from '../../../models/FileModel';
import tokenDurations from '../../../constants/tokenDurations';
import { authToken } from '../../../utilities/LocalStorage';
import AppUtils from '../../../utilities/AppUtils';
import PasswordStrength from '../../../utilities/PasswordStrength';

export const EnableMfaFlowActions = {

  init() {
    return _.assign({}, {
      type: actionNames.emfaf_init,
      payload: {},
    });
  },

  mobileNumber(mobileNumber, isValid) {
    return _.assign({}, {
      type: actionNames.emfaf_update,
      payload: {
        mobileNumber,
        mobileNumberIsValid: isValid,
      },
    });
  },

  country(country, isValid) {
    return _.assign({}, {
      type: actionNames.emfaf_update,
      payload: {
        country,
        mobileNumberIsValid: isValid,
      },
    });
  },

  mfaCode(mfaCode) {
    return _.assign({}, {
      type: actionNames.emfaf_update,
      payload: {
        mfaCode,
        mfaCodeIsValid: mfaCode.length > 5,
      },
    });
  },

  submitMobileNumber(callback) {
    return function asyncAction(dispatch, getState) {
      const user = getLoggedInUser(getState());
      const enableMfaData = getEnableMfaFlowData(getState());
      const enableMfaModel = new EnableMfaModel(enableMfaData);

      return new Promise((resolve, reject) => {
        UserService.setMobileNumber(user.data.id, enableMfaModel.bundleForSetMobileNumberApi(), (apiError) => {
          if (apiError) {
            reject();
          } else {
            callback();
            resolve();
          }
        });
      });

        // SessionService.changePassword({
        //   email: user.data.email,
        //   password: changePasswordFlowData.currentPassword,
        // }, changePasswordFlowData.newPassword, 'long', (apiError) => {
        //   if (apiError) {
        //     reject();
        //   } else {
        //     dispatch({
        //       type: actionNames.cpf_reset,
        //       payload: {},
        //     });
        //     resolve();
        //   }
        // });
      // });
    };
  },

  disableMfa(callback) {
    const user = getLoggedInUser(store.getState());

    UserService.disableMfa(user.data.id, () => {
      user.clearMfa();

      UserService.get(user.data.id, (apiError2, updatedUser) => {
        if (!apiError2) {
          store.dispatch(updateMainUser(updatedUser.data));
          callback();
        }
      });
    });
  },

  enableMfa(mfaCode, callback) {
    return function asyncAction(dispatch, getState) {
      const user = getLoggedInUser(getState());
      const enableMfaData = getEnableMfaFlowData(getState());
      const enableMfaModel = new EnableMfaModel(enableMfaData);

      return new Promise((resolve) => {
        SessionService.enableMfa(enableMfaModel.getMfaCode(), (apiError1) => {
          if (apiError1) {
            callback(apiError1);
          } else {
            UserService.get(user.data.id, (apiError2, updatedUser) => {
              if (!apiError2) {
                dispatch(updateMainUser(updatedUser.data));
                dispatch({
                  type: actionNames.emfaf_reset,
                  payload: {},
                });
              }

              // store.dispatch(clearPendingEdit());
              callback(apiError2);
              resolve();
            });
          }
        });
      });
    };
  },

  undoMobileNumber() {
    return _.assign({}, {
      type: actionNames.emfaf_update,
      payload: {
        mobileNumberSubmitted: false,
      },
    });
  },

  reset() {
    return _.assign({}, {
      type: actionNames.emfaf_reset,
      payload: {},
    });
  },
};

export const ChangePasswordFlowActions = {

  currentPassword(value) {
    return _.assign({}, {
      type: actionNames.cpf_currentPassword,
      payload: {
        currentPasswordIsValid: value.length > 1,
        currentPassword: value,
        passwordsSubmitted: false,
        passwordSubmitSuccessful: false,
        mfaCodeSubmittedSuccessful: false,
      },
    });
  },

  newPassword(value, passwordStrength) {
    return _.assign({}, {
      type: actionNames.cpf_newPassword,
      payload: {
        newPasswordIsValid: PasswordStrength.isStrongEnough(passwordStrength),
        newPassword: value,
        passwordStrength,
        passwordsSubmitted: false,
        passwordSubmitSuccessful: false,
        mfaCodeSubmittedSuccessful: false,
      },
    });
  },

  reset() {
    store.dispatch({
      type: actionNames.cpf_reset,
      payload: {},
    });
  },

  mfaCode(value) {
    return _.assign({}, {
      type: actionNames.cpf_update,
      payload: {
        mfaCode: value,
        mfaCodeIsValid: value.length > 5,
      },
    });
  },

  undoSubmitPassword() {
    return _.assign({}, {
      type: actionNames.cpf_update,
      payload: {
        passwordSubmitSuccessful: false,
      },
    });
  },

  submitPasswords(callback) {
    return function asyncAction(dispatch, getState) {
      const user = getLoggedInUser(getState());

      const { duration } = AppUtils.extractDataFromAuthToken(authToken.get());

      const changePasswordFlowData = getChangePasswordFlowData(getState());
      return new Promise((resolve) => {
        SessionService.changePassword({
          email: user.data.email,
          password: changePasswordFlowData.currentPassword,
        }, changePasswordFlowData.newPassword, duration, (apiError) => {
          if (apiError) {
            if (apiError.messageKey() === 'error.api-sessions.401-invalid-current-pwd.message') {
              dispatch(systemNotificationActions.showNotification(apiError.messageKey()));
              // invalid pwd
            } else if (apiError.messageKey() === 'error.api-sessions.400-mfa-failure.message') {
              dispatch({
                type: actionNames.cpf_passwordsSubmitted,
                payload: {},
              });
            }
          } else {
            dispatch({
              type: actionNames.cpf_reset,
              payload: {},
            });
          }
          callback(apiError);
          resolve();
        });
      });
    };
  },

  submitPasswordsWithMfa(callback) {
    return function asyncAction(dispatch, getState) {
      const user = getLoggedInUser(getState());
      const changePasswordFlowData = getChangePasswordFlowData(getState());
      return new Promise((resolve) => {
        SessionService.mfaChangePassword({
          email: user.data.email,
          password: changePasswordFlowData.currentPassword,
        }, changePasswordFlowData.mfaCode, changePasswordFlowData.newPassword, tokenDurations.SHORT, (apiError) => {
          if (!apiError) {
            dispatch({
              type: actionNames.cpf_reset,
              payload: {},
            });
          }

          callback(apiError);
          resolve();
        });
      });
    };
  },

  mfaCodeSubmitted(options) {
    return _.assign({}, {
      type: actionNames.cpf_mfaCodeSubmitted,
      payload: options,
    });
  },

  passwordSubmitSuccessful(options) {
    return _.assign({}, {
      type: actionNames.cpf_passwordSubmitSuccessful,
      payload: options,
    });
  },

  mfaCodeSubmittedSuccessful(options) {
    return _.assign({}, {
      type: actionNames.cpf_mfaCodeSubmittedSuccessful,
      payload: options,
    });
  },

  init() {
    return _.assign({}, {
      type: actionNames.cpf_init,
      payload: {},
    });
  },

};

export const FileUploadFlow = {

  selectedRawFile(rawFile) {
    const file = FileModel.createFromRawFile(rawFile).data;
    return _.assign({}, {
      type: actionNames.fuf_update,
      payload: {
        file,
        rawFile,
      },
    });
  },

};
